// src/components/common/KanbanBoard.jsx
import React from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const KanbanBoard = ({ 
  columns = [], 
  items = [], 
  groupBy = "status", 
  onDragEnd, 
  renderCard 
}) => {
  
  // Helper to filter items for a specific column
  const getItemsForColumn = (columnId) => {
    return items.filter(item => String(item[groupBy]) === String(columnId));
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      {/* 🟢 CHANGED: Added w-full to ensure the container spans the whole layout */}
      <div className="flex h-full w-full overflow-x-auto overflow-y-hidden gap-4 pb-4 items-start pt-2 px-1 [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
        
        {columns.map((col) => {
          const columnItems = getItemsForColumn(col.id);

          return (
            <div 
              key={col.id} 
              // 🟢 CHANGED: Replaced w-[340px] with flex-1 min-w-[280px] max-w-[500px]
              // This makes columns stretch to fill empty space, but shrink nicely on small screens!
              className="flex flex-col flex-1 min-w-[280px] max-w-[500px] max-h-full bg-[#f4f5f7] dark:bg-[#161b22] rounded-xl border border-gray-200 dark:border-[#30363d] shadow-xs"
            >
              {/* --- COLUMN HEADER --- */}
              <div className="px-4 py-3 border-b border-gray-200 dark:border-[#30363d] flex justify-between items-start bg-[#f8fafc] dark:bg-[#161b22] rounded-t-xl shrink-0">
                <div>
                  <div className="flex items-center gap-2">
                    {col.icon && <col.icon className={`w-5 h-5 ${col.iconColor || 'text-gray-500'}`} />}
                    {col.dotColor && <span className={`w-2 h-2 rounded-full ${col.dotColor}`}></span>}
                    <h3 className="font-bold text-base text-slate-800 dark:text-white uppercase tracking-wide">
                      {col.title || col.label}
                    </h3>
                  </div>
                  {col.description && (
                    <p className="text-[10px] text-gray-500 dark:text-slate-400 mt-1 font-medium">{col.description}</p>
                  )}
                </div>
                <span className="bg-white dark:bg-[#21262d] border border-gray-200 dark:border-[#30363d] text-gray-700 dark:text-slate-200 text-xs font-bold px-2 py-0.5 rounded shadow-xs">
                  {columnItems.length}
                </span>
              </div>

              {/* --- DROP ZONE --- */}
              <Droppable droppableId={String(col.id)}>
                {(provided, snapshot) => (
                  <div 
                    ref={provided.innerRef} 
                    {...provided.droppableProps}
                    className={`flex-1 overflow-y-auto p-3 space-y-3 min-h-[150px] transition-colors ${
                      snapshot.isDraggingOver ? "bg-indigo-50/50 dark:bg-indigo-950/20" : ""
                    }`}
                  >
                    {columnItems.map((item, index) => (
                      <Draggable 
                        key={String(item.id)} 
                        draggableId={String(item.id)} 
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={`transition-all ${snapshot.isDragging ? "rotate-2 scale-105 shadow-xl ring-2 ring-indigo-400 rounded-lg z-50" : ""}`}
                            style={{ ...provided.draggableProps.style }}
                          >
                            {/* Injects your custom card design here */}
                            {renderCard(item)}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    
                    {/* Empty State */}
                    {columnItems.length === 0 && (
                      <div className={`h-24 border-2 border-dashed rounded-lg flex flex-col items-center justify-center transition-colors ${
                        snapshot.isDraggingOver ? 'border-indigo-400 dark:border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/20' : 'border-gray-300 dark:border-[#30363d]'
                      }`}>
                        <p className="text-gray-400 dark:text-slate-500 text-[11px] font-bold uppercase tracking-wider">
                          {snapshot.isDraggingOver ? 'Drop Here' : 'Drag items here'}
                        </p>
                      </div>
                    )}
                    
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>

              {/* --- COLUMN FOOTER (Optional) --- */}
              {col.renderFooter && (
                <div className="px-4 py-3 bg-white dark:bg-[#161b22] border-t border-gray-200 dark:border-[#30363d] rounded-b-xl shrink-0">
                  {col.renderFooter(columnItems)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
};

export default KanbanBoard;